import { useSiteReportConfigurationState } from "../useSiteReportConfiguration/useSiteReportConfiguration";

export interface CheckboxHierarchyState {
  getCheckboxState: (path: string) => boolean;
  setCheckboxState: (path: string, value: boolean) => void;
  setBulkCheckboxState: (updates: Record<string, boolean>) => void;
  toggleCheckbox: (path: string) => void;
  updateParentState: (path: string, allPaths: string[]) => void;
}

/**
 * Hook that manages checkbox state for the hierarchical site report structure.
 *
 * This hook provides a simplified interface to the underlying configuration state,
 * specifically for managing checkbox selections. It handles:
 * - Individual checkbox state management
 * - Bulk updates for performance
 * - Parent state synchronization
 * - Path-based state access
 *
 * Use cases:
 * - Managing user selections in the site report UI
 * - Persisting checkbox states across sessions
 * - Synchronizing parent-child checkbox relationships
 * - Bulk operations (select all, deselect all)
 *
 * @param siteId - The ID of the current site
 * @param allLayerIds - Array of all available layer IDs for parent state updates
 * @returns Object with methods to manage checkbox state
 */
export function useCheckboxHierarchy(siteId: string, allLayerIds: string[]): CheckboxHierarchyState {
  const checkboxState = useSiteReportConfigurationState("checkboxPaths", siteId, allLayerIds);

  return {
    getCheckboxState: checkboxState.getCheckboxState,
    setCheckboxState: checkboxState.setCheckboxState,
    setBulkCheckboxState: checkboxState.setBulkCheckboxState,
    toggleCheckbox: checkboxState.toggleCheckbox,
    updateParentState: checkboxState.updateParentState,
  };
}

// Export the path utilities from the shared location for backward compatibility
export { buildCheckboxPath, parseCheckboxPath } from "../useSiteReportConfiguration/pathUtils";
export type { CheckboxPath } from "../useSiteReportConfiguration/pathUtils";
